// NEW CODE
import { useState, useEffect } from 'react';
import { collection, query, where, orderBy, limit, getDocs } from 'firebase/firestore';
import { db } from '../firebase';
import { getAuth } from 'firebase/auth';


const useRecentSearches = (fetchRecentSearches, maxDisplayedSearches = 10) => {
  const [recentSearches, setRecentSearches] = useState([]);
  const [filteredRecentSearches, setFilteredRecentSearches] = useState([]);
  const [loading, setLoading] = useState(false);

  // Filter recent searches based on input query
  const handleSearchFiltering = (query, searches = recentSearches) => {
    if (!Array.isArray(searches)) {
      console.warn('Received invalid searches:', searches);
      return setFilteredRecentSearches([]);
    }

    const filtered = searches.filter(search => {
      if (!search || typeof search.userQuery !== 'string') {
        console.warn('Invalid search entry:', search);
        return false;
      }
      try {
        return search.userQuery.toLowerCase().includes((query || '').toLowerCase());
      } catch (error) {
        console.warn('Error filtering search:', error);
        return false;
      }
    });
    setFilteredRecentSearches(filtered.slice(0, maxDisplayedSearches));
  };

  // Load searches from localStorage
  const loadFromLocalStorage = () => {
    const storedSearches = localStorage.getItem('recentSearches');
    if (storedSearches) {
      try {
        const parsedSearches = JSON.parse(storedSearches);
        setRecentSearches(parsedSearches);
        // For empty query, show all searches up to max limit
        setFilteredRecentSearches(parsedSearches.slice(0, maxDisplayedSearches));
      } catch (error) {
        console.warn('Error parsing stored searches:', error);
        setRecentSearches([]);
        setFilteredRecentSearches([]);
      }
    }
  };

  // NEW FUNCTION: Fetch directly from Firestore
  const fetchFromFirestore = async () => {
    setLoading(true);
    try {
      // Get current user's email
      const auth = getAuth();
      const userEmail = auth.currentUser?.email;

      // If no user is logged in, return empty array
      if (!userEmail) {
        console.warn('No user is currently logged in');
        setLoading(false);
        return [];
      }

      // Query the LLM Queries collection in Firestore for only the current user's queries
      const q = query(
        collection(db, 'LLM Queries'),
        where('userId', '==', userEmail),
        where('status', '==', 'completed'),
        orderBy('endTime', 'desc'),
        limit(30)
      );

      const snapshot = await getDocs(q);
      const searches = snapshot.docs.map(doc => {
        const data = doc.data();
        return {
          id: doc.id,
          userQuery: data.userQuestion || '',
          sqlQuery: data.code || '',
          timestamp: data.endTime || data.startTime, // Prioritize endTime to match the sorting
          documentId: doc.id,
          llmModel: data.llmModel || '',
          fallbackUsed: data.fallbackUsed || false
        };
      });

      setRecentSearches(searches);
      handleSearchFiltering('', searches);
      localStorage.setItem('recentSearches', JSON.stringify(searches));
      return searches;
    } catch (error) {
      console.error('Error fetching from Firestore:', error);
      return null;
    } finally {
      setLoading(false);
    }
  };

// Refresh searches - using only direct Firestore approach
const refreshRecentSearches = async () => {
  // Only use the direct Firestore approach
  try {
    const firestoreSearches = await fetchFromFirestore();
    return firestoreSearches || [];
    
    /* Backend function call disabled
    if (!firestoreSearches || firestoreSearches.length === 0) {
      // Fall back to backend function
      if (fetchRecentSearches) {
        const searches = await fetchRecentSearches();
        if (searches) {
          setRecentSearches(searches);
          handleSearchFiltering('', searches);
          localStorage.setItem('recentSearches', JSON.stringify(searches));
        }
        return searches;
      }
    }
    */
  } catch (error) {
    console.warn('Firestore fetch failed:', error);
    // No longer falling back to backend function
    return [];
  }
};

  // Clear recent searches and reload
  const clearRecentSearches = async () => {
    setRecentSearches([]);
    setFilteredRecentSearches([]);
    
    // Load from localStorage first
    loadFromLocalStorage();
    
    // Then fetch fresh data
    await refreshRecentSearches();
  };

  // Initial load effect
  useEffect(() => {
    loadFromLocalStorage();
    refreshRecentSearches();
  }, []);

  return {
    recentSearches,
    filteredRecentSearches,
    handleSearchFiltering,
    refreshRecentSearches,
    clearRecentSearches,
    loading,
    fetchFromFirestore // Export the new function
  };
};

export default useRecentSearches;